import { useCallback, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { lineTotal } from "@/lib/quote-totals";
import type { LineItemDraft } from "./QuoteLineItemRow";
import type { PickedItem } from "./QuoteItemPicker";
import type { FormLineItem } from "./SortableLineItemList";

// Every line item entering form state gets a stable key so its row (and any
// input the user is typing into) stays with it when the list is reordered.
const withKey = (li: LineItemDraft): FormLineItem => ({ ...li, key: crypto.randomUUID() });

/**
 * The line-item list shared by the quote form and the template editor (#1345),
 * so both add, edit, total and reorder lines by the same rules.
 */
export function useLineItemDrafts(initial?: () => LineItemDraft[]) {
  const [lineItems, setLineItems] = useState<FormLineItem[]>(() => (initial ? initial().map(withKey) : []));

  const replaceLines = useCallback((drafts: LineItemDraft[]) => setLineItems(drafts.map(withKey)), []);

  // Existing lines keep their keys; the new ones go after them (#1547).
  const appendLines = useCallback(
    (drafts: LineItemDraft[]) => setLineItems((prev) => [...prev, ...drafts.map(withKey)]),
    [],
  );

  const addPickedItem = useCallback((item: PickedItem) => setLineItems((prev) => [...prev, withKey(item)]), []);

  const addCustomLine = useCallback(
    (name: string) =>
      setLineItems((prev) => [
        ...prev,
        withKey({ name, description: "", pricing_type: "custom", unit_price: 0, quantity: 1, line_total: 0 }),
      ]),
    [],
  );

  const updateLine = useCallback((i: number, field: string, value: string | number) => {
    setLineItems((lines) =>
      lines.map((li, idx) => {
        if (idx !== i) return li;
        const updated = { ...li, [field]: value };
        updated.line_total = lineTotal(updated.unit_price, updated.quantity);
        return updated;
      }),
    );
  }, []);

  const removeLine = useCallback((i: number) => setLineItems((l) => l.filter((_, idx) => idx !== i)), []);

  const reorderLines = useCallback((from: number, to: number) => setLineItems((l) => arrayMove(l, from, to)), []);

  // Per-person lines follow the guest count.
  const resizePerPerson = useCallback((guests: number) => {
    setLineItems((prev) =>
      prev.map((li) =>
        li.pricing_type === "per_person" ? { ...li, quantity: guests, line_total: lineTotal(li.unit_price, guests) } : li,
      ),
    );
  }, []);

  return {
    lineItems,
    replaceLines,
    appendLines,
    addPickedItem,
    addCustomLine,
    updateLine,
    removeLine,
    reorderLines,
    resizePerPerson,
  };
}

/**
 * Escape during a keyboard drag should cancel the drag, not close the dialog.
 * Pass `onDraggingChange` to SortableLineItemList and `onEscapeKeyDown` to DialogContent.
 */
export function useDragEscapeGuard() {
  const [dragging, setDragging] = useState(false);
  const onEscapeKeyDown = (e: KeyboardEvent) => {
    if (dragging) e.preventDefault();
  };
  return { onDraggingChange: setDragging, onEscapeKeyDown };
}
